import {
  addToast,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { api } from "@/lib/api-client";
import {
  findIssuedEstimateId,
  getEstimateConversionUrl,
} from "./subscriptionInvoiceUtils";

type Props = {
  shareId: number | null;
  /** Display only: the copy differs for a company's share and the customer's. */
  payerCompanyId?: number | null;
  payerName?: string | null;
  /** Where the POS returns once the invoice is issued. */
  redirectUrl: string;
  onClose: () => void;
};

/** The POS links an invoice to a period only through an estimate, so one is issued unsent and converted at once. */
export function SubscriptionInvoiceIssueInvoiceModal({
  shareId,
  payerCompanyId = null,
  payerName = null,
  redirectUrl,
  onClose,
}: Props) {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { mutateAsync: issueEstimate, isPending } =
    api.customer.useIssueCustomerSubscriptionEstimates();

  const handleConfirm = async () => {
    if (shareId == null) {
      Sentry.captureMessage(
        "SubscriptionInvoiceIssueInvoiceModal: handleConfirm aborted — missing shareId",
        "warning",
      );
      return;
    }
    try {
      const { documents } = await issueEstimate({
        body: { shareIds: [shareId], sendEstimateOnEmail: false },
      });
      const estimateId = findIssuedEstimateId(documents, shareId);
      if (estimateId == null) {
        Sentry.captureMessage(
          "SubscriptionInvoiceIssueInvoiceModal: issued estimate does not cover the share",
          { level: "error", extra: { shareId, documents } },
        );
        addToast({
          title: t`Blagajne ni bilo mogoče odpreti`,
          description: t`Preverite obdobje v tabeli: če ima predračun, ga pretvorite v račun, sicer poskusite ponovno.`,
          color: "danger",
        });
        onClose();
        return;
      }
      setIsRedirecting(true);
      window.location.href = getEstimateConversionUrl(estimateId, redirectUrl);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <LimeModal
      isOpen={shareId != null}
      onClose={onClose}
      data-identifier="subscription-invoice-issue-invoice-modal"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              <Trans>Izdaj račun</Trans>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-2">
                <p className="text-default-700 text-sm">
                  {payerCompanyId != null && payerName ? (
                    <Trans>
                      Za delež podjetja {payerName} v tem obdobju se odpre
                      blagajna, kjer izberete način plačila in izdate račun.
                    </Trans>
                  ) : (
                    <Trans>
                      Za to obdobje se odpre blagajna, kjer izberete način
                      plačila in izdate račun.
                    </Trans>
                  )}
                </p>
                <p className="text-default-500 text-tiny">
                  <Trans>
                    Račun se izda iz predračuna, ki se ustvari samodejno in se
                    ne pošlje po e-pošti. Če računa ne izdate, ostane obdobje
                    povezano s predračunom.
                  </Trans>
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                onPress={onClose}
                isDisabled={isRedirecting}
              >
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                color="primary"
                onPress={handleConfirm}
                isLoading={isPending || isRedirecting}
                data-identifier="confirm-issue-invoice-button"
              >
                <Trans>Nadaljuj v blagajno</Trans>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </LimeModal>
  );
}
